import { RequisitoConAsesoria } from '@/services/requisitos.service';
import type { CambioRequisito } from '@/types/evaluacion';
import { createWithEqualityFn } from 'zustand/traditional';
import { persist } from 'zustand/middleware';

const emptyRequisito: CambioRequisito = {
  calificacion: undefined,
};

function esIgualCampo<T>(a: T, b: T): boolean {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => v === b[i]);
  }
  return a === b;
}

interface EvaluacionState {
  cambios: Record<string, CambioRequisito>;
  revertidos: Record<string, string>; // reqId → calificacionAnterior; pending backend DELETE
  valoresOriginales: Record<string, CambioRequisito>;
  /** Calificación real de la asesoría ANTERIOR (baseline), independiente de lo que ya esté
   * persistido para ESTA asesoría. Necesaria para detectar reversiones incluso cuando
   * `valoresOriginales` (snapshot de esta sesión) ya refleja un valor intermedio que se
   * autoguardó antes de volver al valor real de siempre — ver bug: NC→CP autoguardado,
   * vuelto a NC sin usar el botón "Restaurar", dejaba un EvaluacionRequisito huérfano en
   * PENDIENTE bloqueando la aprobación aunque el diff visual no mostrara ningún cambio. */
  baseline: Record<string, string | null>;
  asesoriaId: string | null;
  inicializado: boolean;

  initOriginales: (asesoriaId: string, requisitos: RequisitoConAsesoria[]) => void;
  setCampo: <K extends keyof CambioRequisito>(reqId: string, campo: K, valor: CambioRequisito[K]) => void;
  marcarRevertido: (reqId: string, calificacionAnterior: string) => void;
  limpiarCambios: () => void;
  limpiarCambiosEspecificos: (reqIds: string[]) => void;
  limpiarRevertidos: (reqIds: string[]) => void;
  resetStore: () => void;
  getValor: <K extends keyof CambioRequisito>(reqId: string, campo: K) => CambioRequisito[K] | undefined;
  getValores: (reqId: string) => CambioRequisito;
}

const store = createWithEqualityFn<EvaluacionState>()(
  persist(
    (set, get) => ({
      cambios: {},
      revertidos: {},
      valoresOriginales: {},
      baseline: {},
      asesoriaId: null,
      inicializado: false,

      initOriginales: (asesoriaId, requisitos) => {
        set((state) => {
          console.log('Inicializando store para asesoría:', asesoriaId, 'con', requisitos.length, 'requisitos');

          const mapa: Record<string, CambioRequisito> = {};
          const baseline: Record<string, string | null> = {};
          for (const r of requisitos) {
            // Solo calificación — observaciones/plan/evidencias van en EvaluacionTerna
            const ev = r.evaluacionRequisito;
            mapa[r.id] = {
              calificacion: ev?.calificacion || r.calificacion,
            };
            baseline[r.id] = r.calificacionAnterior ?? null;
          }

          console.log('Valores originales inicializados:', Object.keys(mapa).length, 'requisitos');

          return {
            valoresOriginales: mapa,
            baseline,
            cambios: state.asesoriaId === asesoriaId ? state.cambios : {},
            revertidos: state.asesoriaId === asesoriaId ? state.revertidos : {},
            asesoriaId,
            inicializado: true,
          };
        });
      },

      resetStore: () => {
        console.log('Reseteando store de evaluación');
        set({
          cambios: {},
          revertidos: {},
          valoresOriginales: {},
          baseline: {},
          asesoriaId: null,
          inicializado: false,
        });
      },

      marcarRevertido: (reqId, calificacionAnterior) => {
        set((state) => {
          const { [reqId]: _, ...sinReq } = state.cambios;
          return {
            cambios: sinReq,
            revertidos: { ...state.revertidos, [reqId]: calificacionAnterior },
          };
        });
      },

      setCampo: (reqId, campo, valor) => {
        set((state) => {
          const original = state.valoresOriginales[reqId];
          const cambioActual = state.cambios[reqId] ?? {};
          const esOriginal = esIgualCampo(valor, original?.[campo]);

          // Para calificación: si el valor nuevo coincide con el baseline REAL (asesoría
          // anterior), es una reversión de verdad — hay que pedirle al backend que borre
          // cualquier EvaluacionRequisito ya autoguardado, no solo sacarlo de `cambios`. Sin
          // esto, un NC→CP autoguardado y luego vuelto a NC (sin usar "Restaurar") dejaba un
          // registro huérfano en PENDIENTE bloqueando la aprobación, aunque el diff visual
          // (que sí compara contra el baseline) no mostrara ningún cambio.
          if (campo === 'calificacion') {
            const baseline = state.baseline[reqId];
            const esBaseline = baseline !== undefined && esIgualCampo(valor as unknown, baseline as unknown);
            if (esBaseline) {
              const { [reqId]: _, ...sinReqCambios } = state.cambios;
              return {
                cambios: sinReqCambios,
                revertidos: { ...state.revertidos, [reqId]: valor as string },
              };
            }
            if (state.revertidos[reqId] !== undefined) {
              // Se alejó del baseline: ya no es una reversión, es un cambio real a guardar.
              const { [reqId]: _, ...sinReqRevertidos } = state.revertidos;
              return {
                revertidos: sinReqRevertidos,
                cambios: { ...state.cambios, [reqId]: { ...cambioActual, [campo]: valor } },
              };
            }
          }

          if (esOriginal) {
            const { [campo]: _, ...resto } = cambioActual;

            if (Object.keys(resto).length === 0) {
              const { [reqId]: __, ...sinReq } = state.cambios;
              return { cambios: sinReq };
            }

            return { cambios: { ...state.cambios, [reqId]: resto } };
          }

          return {
            cambios: {
              ...state.cambios,
              [reqId]: { ...cambioActual, [campo]: valor },
            },
          };
        });
      },

      limpiarCambios: () => set({ cambios: {}, revertidos: {} }),

      limpiarCambiosEspecificos: (reqIds) => {
        set((state) => {
          const nuevosCambios = { ...state.cambios };
          reqIds.forEach((reqId) => {
            delete nuevosCambios[reqId];
          });
          return { cambios: nuevosCambios };
        });
      },

      limpiarRevertidos: (reqIds) => {
        set((state) => {
          const nuevosRevertidos = { ...state.revertidos };
          reqIds.forEach((reqId) => {
            delete nuevosRevertidos[reqId];
          });
          return { revertidos: nuevosRevertidos };
        });
      },

      getValor: (reqId, campo) => {
        const state = get();
        const valor = state.cambios[reqId]?.[campo] ?? state.valoresOriginales[reqId]?.[campo];
        return valor;
      },

      getValores: (reqId) => {
        const state = get();
        const original = state.valoresOriginales[reqId] ?? emptyRequisito;
        const cambios = state.cambios[reqId] ?? {};
        return { ...original, ...cambios };
      },
    }),
    {
      name: 'evaluacion-draft',
      partialize: (state) => ({
        cambios: state.cambios,
        asesoriaId: state.asesoriaId,
      }),
    }
  ),
  Object.is
);

export const useEvaluacionStore = store;

export function useCampoRequisito<K extends keyof CambioRequisito>(reqId: string, campo: K): CambioRequisito[K] | undefined {
  return useEvaluacionStore((s) => {
    const valor = s.cambios[reqId]?.[campo] ?? s.valoresOriginales[reqId]?.[campo];
    return valor;
  });
}

// Hook para obtener todo el estado de un requisito
export function useRequisito(reqId: string): CambioRequisito {
  return useEvaluacionStore((s) => {
    const original = s.valoresOriginales[reqId] ?? emptyRequisito;
    const cambios = s.cambios[reqId] ?? {};
    return { ...original, ...cambios };
  });
}

// Export the store for direct access
export { store as evaluacionStore };
